import { Box, Divider, Tooltip } from "@mui/material";
import { useRef, useState } from "react";
import CustomTextArea from "@/components/CustomTextArea";
import Flex from "@/components/Flex";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import { useChatAttachmentUpload } from "@/queries/communication/chat/chatting/attachment/use-chat-attachment";
import EmojiPopover from "@/zoom/chat/component/EmojiPopover";
import { ChatUser } from "../messages/ChatUser";
import { ChatConsentInfo } from "../messages/ConsentInfoBanner";
import { SendButton } from "../messages/SendButton";
import AttachmentPreview from "./AttachmentPreview";
import { Message } from "./Chat";
import ChatAttachment from "./ChatAttachment";
import ChatBox from "./ChatBox";
import { ChatConversation, DISABLED_RECEIVER_TOOLTIP } from "./chat-constant";

interface ChatContainerProps {
  user: ChatConversation;
  onSend: (
    msg: string,
    attachment?: string | null,
    attachmentExt?: string | null,
    fileName?: string | null
  ) => Promise<any>;
  messages?: Record<string, Message[]>;
  hideTopBar?: boolean;
}

type PendingAttachment = {
  file: File;
  fileType: "file" | "image";
  id: string;
};

export const ChatContainer = ({
  user,
  onSend,
  messages,
  hideTopBar,
}: ChatContainerProps) => {
  const [msg, setMsg] = useState<string>("");
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const [me] = useCurrentUser();
  const { mutateAsync: uploadChatAttachment } = useChatAttachmentUpload();
  const msgInputRef = useRef<HTMLTextAreaElement | null>(null);

  const senderName =
    (user?.participant2 === me?.uuid ? user?.senderName : user?.receiverName) ??
    "";

  const isReceiverDisabled = user?.receiverActive === false;
  const isComposeDisabled = uploading || isReceiverDisabled;

  const handleAttach = (
    _file: File | null,
    fileExtension: "file" | "image"
  ) => {
    if (!_file) return;

    // Add file to pending attachments for preview
    const newAttachment: PendingAttachment = {
      file: _file,
      fileType: fileExtension,
      id: `${Date.now()}-${Math.random()}`,
    };
    setPendingAttachments((prev) => [...prev, newAttachment]);
  };

  const handleRemoveAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  const handleSend = async () => {
    const hasMessage = msg?.trim();
    const hasAttachments = pendingAttachments.length > 0;

    if (isReceiverDisabled) return;
    if (!hasMessage && !hasAttachments) return;

    setUploading(true);
    try {
      // Upload all attachments first
      if (hasAttachments) {
        const uploadPromises = pendingAttachments.map(async (attachment) => {
          const res: any = await uploadChatAttachment(attachment.file);
          return {
            attachmentUrl: res?.data?.data?.s3Url as string,
            fileName: res?.data?.data?.fileName as string,
            fileType: attachment.fileType,
          };
        });

        const uploadedAttachments = await Promise.all(uploadPromises);

        // Send message with attachments
        // If there's text and attachments, send text with the first attachment
        // Then send remaining attachments separately
        if (hasMessage && uploadedAttachments.length > 0) {
          // Send text with first attachment
          const firstAttachment = uploadedAttachments[0];
          await onSend(
            msg,
            firstAttachment.attachmentUrl,
            firstAttachment.fileType,
            firstAttachment.fileName
          );

          // Send remaining attachments separately
          for (let i = 1; i < uploadedAttachments.length; i++) {
            const attachment = uploadedAttachments[i];
            await onSend(
              "",
              attachment.attachmentUrl,
              attachment.fileType,
              attachment.fileName
            );
          }
        } else {
          // No text message, send all attachments separately
          for (const attachment of uploadedAttachments) {
            await onSend(
              "",
              attachment.attachmentUrl,
              attachment.fileType,
              attachment.fileName
            );
          }
        }
      } else {
        // No attachments, just send the message
        await onSend(msg, null, null, null);
      }

      // Clear message and attachments after successful send
      setMsg("");
      setPendingAttachments([]);
    } finally {
      setUploading(false);
      setTimeout(() => msgInputRef.current?.focus(), 0);
    }
  };

  return (
    <Flex
      flexDirection={"column"}
      width={"100%"}
      height={"100%"}
      position={"relative"}
    >
      {!hideTopBar && (
        <Flex alignItems={"center"} pr={1} height={"68px"}>
          <ChatUser name={senderName} />
        </Flex>
      )}

      {!hideTopBar && (
        <Divider
          sx={{ height: "1px", backgroundColor: "gray" }}
          orientation="horizontal"
        />
      )}

      <Flex
        p={1}
        flexDirection={"column"}
        flex={1}
        height={"calc(100svh - 16.5rem)"}
        sx={{ overflowY: "scroll" }}
        gap={2}
      >
        <ChatBox messages={messages as any} senderName={senderName} />
      </Flex>

      <ChatConsentInfo sx={{ m: 1.2 }} />

      {/* Attachment Preview Section */}
      {pendingAttachments.length > 0 && (
        <Flex gap={1} px={1.2} pb={1} flexWrap={"wrap"}>
          {pendingAttachments.map((attachment) => (
            <AttachmentPreview
              key={attachment.id}
              file={attachment.file}
              fileType={attachment.fileType}
              onRemove={() => handleRemoveAttachment(attachment.id)}
            />
          ))}
        </Flex>
      )}

      <Flex gap={2} alignItems={"center"} px={1.2} mb={1}>
        <Box>
          <ChatAttachment
            onAttach={handleAttach}
            disabled={isComposeDisabled}
          />
        </Box>

        <Tooltip
          title={isReceiverDisabled ? DISABLED_RECEIVER_TOOLTIP : ""}
          placement="top"
        >
          <Box flex={1}>
            <CustomTextArea
              maxRows={7}
              sx={{
                borderRadius: "40px",
                // a disabled input fires no mouse events, so let them through
                // to the wrapper that anchors the tooltip
                ...(isReceiverDisabled && { pointerEvents: "none" }),
              }}
              fullWidth
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              placeholder="Type your message..."
              disabled={isComposeDisabled}
              inputRef={msgInputRef}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey && !isComposeDisabled) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
          </Box>
        </Tooltip>
        <Box
          sx={{
            pointerEvents: isReceiverDisabled ? "none" : "auto",
            opacity: isReceiverDisabled ? 0.5 : 1,
          }}
        >
          <EmojiPopover
            value={msg}
            onChange={(v) => {
              setMsg(v);
              // Picking an emoji closes the popover and moves focus to its
              // trigger; restore focus to the input and place the caret right
              // after the inserted emoji (appended at the end) so typing
              // continues from there.
              setTimeout(() => {
                const el = msgInputRef.current;
                if (!el) return;
                el.focus();
                const caret = el.value.length;
                el.setSelectionRange(caret, caret);
              }, 0);
            }}
          />
        </Box>
        <Box>
          <SendButton
            onClick={handleSend}
            disabled={
              isComposeDisabled ||
              (!msg?.trim() && pendingAttachments.length === 0)
            }
          />
        </Box>
      </Flex>
    </Flex>
  );
};
